import { observer } from "owa-mobx-react";
import { SelectAllOnFilled, SelectAllOnRegular, SelectAllOffFilled, SelectAllOffRegular, bundleIcon, } from "@fluentui/react-icons";
import { Button, Tooltip } from "@fluentui/react-components";
import setSelectionSLV from "../mutators/setSelectionSLV";
import { getDensityModeCssClass, getDensityModeString } from "owa-fabric-theme";
import resetSelectAllState from "../mutators/resetSelectAllState";
import loc from "owa-localize";
import { selectLabel } from "owa-locstrings/lib/strings/selectlabel.locstring.json";
import React from "react";
import { selectAllIconFull, selectAllIconMediumAndCompact, full, fullDenseless, medium, compact, singleLineCheckboxButton, selectAllCheckboxButtonWithLabel, selectAllCheckboxButton, selectAllCheckboxButtonMedium, selectAllCheckboxButtonCompact, selectLabelSingleLine, buttonChecked, } from "./MailListHeader.scss";

import { useIconFillOnHover } from "owa-mail-layout";
import classnames from "owa-classnames";
import { useIsTypeRampUpdatesEnabled } from "owa-mail-list-item-shared/lib/utils/useIsTypeRampUpdatesEnabled";
import shouldHideMessageListCheckboxes from "owa-messagelist-layout-option/lib/utils/shouldHideMessageListCheckboxes";

const SelectAllOnIcon = bundleIcon(SelectAllOnFilled, SelectAllOnRegular);
const SelectAllOffIcon = bundleIcon(SelectAllOffFilled, SelectAllOffRegular);

const MAIL_LIST_SELECT_ALL_SLV_ID = "MailListSelectAllSLV";

export interface MailListSelectAllProps {
    tableView: any;
    showButtonLabel: boolean;
}

const defaultExport: React.FunctionComponent<MailListSelectAllProps> = observer(function MailListSelectAllSLV(props: MailListSelectAllProps) {
    const { tableView, showButtonLabel } = props;
    const isFullDensity = getDensityModeString() === "full";
    const isRPDenselessEnabled = useIsTypeRampUpdatesEnabled();
    const shouldDecoupleCheckedModeFromSelectAllState = shouldHideMessageListCheckboxes();
    const { filled, iconColorClassName, onMouseEnter, onMouseLeave } = useIconFillOnHover();
    const densityModeCssClass = isRPDenselessEnabled
        ? fullDenseless
        : getDensityModeCssClass(full, medium, compact);
    
    const isChecked = tableView.isInSelectAllStateSLV || tableView.isInCheckedMode;
    const iconClassName = classnames(isFullDensity || isRPDenselessEnabled
        ? selectAllIconFull
        : selectAllIconMediumAndCompact, iconColorClassName);
    const selectAllIcon = React.useMemo(() => {
        const IconComponent = isChecked ? SelectAllOnIcon : SelectAllOffIcon;
        return <IconComponent filled={filled} className={iconClassName}/>;
    }, [isChecked, filled, iconClassName]);
    
    const onCheckboxClick = React.useCallback(() => {
        setSelectionSLV(tableView, !tableView.isInSelectAllStateSLV);
    }, [tableView, tableView.isInSelectAllStateSLV]);
    
    React.useEffect(() => {
        if (!shouldDecoupleCheckedModeFromSelectAllState) {
            // Only react to checked mode changes, else a manual select-all toggle is undone immediately.
            setSelectionSLV(tableView, tableView.isInCheckedMode);
            resetSelectAllState(tableView);
        }
    }, [tableView, tableView.isInCheckedMode, shouldDecoupleCheckedModeFromSelectAllState]);
    
    React.useEffect(() => {
        if (shouldDecoupleCheckedModeFromSelectAllState) {
            // Keep select-all state independent from checked mode so modifier-click
            // multi-selection does not implicitly enter select-all mode.
            if (!tableView.isInCheckedMode && tableView.isInSelectAllStateSLV) {
                setSelectionSLV(tableView, false);
            }
            resetSelectAllState(tableView);
        }
    }, [
        tableView,
        tableView.isInCheckedMode,
        tableView.isInSelectAllStateSLV,
        shouldDecoupleCheckedModeFromSelectAllState,
    ]);
    
    const onCheckboxKeyDown = React.useCallback((event: React.KeyboardEvent<unknown>) => {
        if (event.key === "Escape") {
            setSelectionSLV(tableView, !tableView.isInSelectAllStateSLV);
        }
    }, [tableView, tableView.isInSelectAllStateSLV]);
    
    if (showButtonLabel) {
        return (<div className={classnames(singleLineCheckboxButton, (tableView.isInSelectAllStateSLV || tableView.isInCheckedMode) &&
                buttonChecked, densityModeCssClass)}>
                    <Button className={classnames(selectAllCheckboxButtonWithLabel, densityModeCssClass)} icon={selectAllIcon} onClick={onCheckboxClick} onKeyDown={onCheckboxKeyDown} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} aria-labelledby={MAIL_LIST_SELECT_ALL_SLV_ID} aria-pressed={isChecked}>
                        <span id={MAIL_LIST_SELECT_ALL_SLV_ID} className={classnames(selectLabelSingleLine, densityModeCssClass)}>
                            {loc(selectLabel)}
                        </span>
                    </Button>
                </div>);
    }
    else {
        return (<div className={densityModeCssClass}>
                    <Tooltip content={loc(selectLabel)} relationship="label" withArrow={true}>
                        <span>
                            <Button className={classnames(selectAllCheckboxButton, (tableView.isInSelectAllStateSLV ||
                tableView.isInCheckedMode) &&
                buttonChecked, isRPDenselessEnabled
                ? selectAllCheckboxButtonMedium
                : getDensityModeCssClass(undefined, selectAllCheckboxButtonMedium, selectAllCheckboxButtonCompact))} icon={selectAllIcon} onClick={onCheckboxClick} onKeyDown={onCheckboxKeyDown} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} aria-label={loc(selectLabel)} aria-pressed={isChecked}></Button>
                        </span>
                    </Tooltip>
                </div>);
    }
}, "MailListSelectAllSLV");
export default defaultExport;
